import React, { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

const INACTIVITY_LIMIT = 20 * 60 * 1000; // 20 minutes
const ACTIVITY_KEY = "user-activity";

const SessionManager: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { logout, getAccessTokenSilently, isAuthenticated, loginWithRedirect } =
    useAuth0();
  const timer = useRef<NodeJS.Timeout | null>(null);

  const broadcastActivity = () => {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
  };

  // Reset inactivity timer
  const resetTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      window.localStorage.removeItem("businessUnitId");
      window.localStorage.removeItem("token");
      window.localStorage.removeItem("antiforgery");
      window.localStorage.removeItem("userName");
      logout({
        logoutParams: { returnTo: process.env.REACT_APP_CALLBACK_URL },
      });      
    }, INACTIVITY_LIMIT);
  };

  // Listen for user activity
  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ["mousemove", "keydown", "mousedown", "touchstart", "wheel", "scroll", "click", "keypress", "touchend", "touchmove", "touchcancel", "focus", "blur", "resize", "select", "change", "input", "contextmenu", "drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop", "copy", "cut", "paste", "submit", "reset", "search", "touchcancel", "pointerdown", "pointerup", "pointermove", "pointerover", "pointerout", "pointerenter", "pointerleave"];
    const handleActivity = () => {
      resetTimer();
      broadcastActivity();
    };
    events.forEach((event) => window.addEventListener(event, handleActivity));
    resetTimer();

    // Listen for activity from other tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVITY_KEY) resetTimer();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      window.removeEventListener("storage", onStorage);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [isAuthenticated]);

  // Listen for tab/window visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Check Auth0 session when tab becomes active
        getAccessTokenSilently().catch(() => {
          loginWithRedirect();
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getAccessTokenSilently, loginWithRedirect]);

  return <>{children}</>;
};

export default SessionManager;
